/**
 * Converts a string to title case.
 *
 * This function transforms any given string to title case, which involves capitalizing the
 * first letter of each word in the string. It specifically handles strings with underscores
 * by first replacing them with spaces, ensuring each 'word' separated by either space or
 * underscore is appropriately capitalized.
 *
 * Example Usage:
 * - Input: "hello_world"
 * - Output: "Hello World"
 *
 */
export const titleCase = (str: string): string => {
  return (
    str
      .trim()
      .replace(/_/g, ' ')
      .toLowerCase()
      .split(' ')
      // Filter out empty strings
      .filter((word) => word)
      .map((word) => word[0].toUpperCase() + word.substring(1))
      .join(' ')
  )
}

/** Checks if a string contains non-ascii characters */
export function hasNonAscii(str: string): boolean {
  // eslint-disable-next-line no-control-regex
  return /[^\x00-\x7F]/.test(str)
}

export const UUID_V4_REGEX_STRING =
  '[a-f0-9]{8}-?[a-f0-9]{4}-?4[a-f0-9]{3}-?[89ab][a-f0-9]{3}-?[a-f0-9]{12}'
/**
 *
 * Regex to match a uuid in a string
 */
export const UUID_V4_REGEX = new RegExp(UUID_V4_REGEX_STRING)

/** Matches any RFC 4122 UUID, not just v4. Lowercase; pass the `i` flag for uppercase ids. */
export const ANY_UUID_REGEX_STRING = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'

// Checks if a string is a boolean
export const isBooleanString = (value: string): boolean => {
  const lowerVal = value.trim().toLowerCase()
  return lowerVal === 'true' || lowerVal === 'false'
}

/**
 * Transforms a text value to a boolean.
 * Accepts boolean values, strings ("true", "yes", "1", "false", "no", "0"), and numbers (1 = true).
 * Returns undefined for null/undefined or invalid values.
 */
export const textToBooleanTransform = (value: unknown): boolean | undefined => {
  if (value == null) {
    return undefined
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim()
    if (lower === 'true' || lower === 'yes' || lower === '1') {
      return true
    }
    if (lower === 'false' || lower === 'no' || lower === '0') {
      return false
    }
  }
  if (typeof value === 'number') {
    return value === 1
  }
  return undefined
}

/**
 * Safely trims a string value, handling null, undefined, and non-string types.
 *
 * @param value - The value to trim (can be string, null, undefined, or other types)
 * @param options - Optional configuration
 * @param options.returnEmptyString - If true, returns empty string for null/undefined/empty. If false, returns undefined (default: false)
 * @returns The trimmed string, or undefined/empty string based on options
 *
 * @example
 * safeTrim("  hello  ") // "hello"
 * safeTrim(null) // undefined
 * safeTrim(undefined) // undefined
 * safeTrim(123) // "123"
 * safeTrim("  ") // undefined (or "" if returnEmptyString: true)
 * safeTrim(null, { returnEmptyString: true }) // ""
 */
export function safeTrim(
  value: string | null | undefined | unknown,
  options?: { returnEmptyString?: false },
): string | undefined
export function safeTrim(
  value: string | null | undefined | unknown,
  options: { returnEmptyString: true },
): string
export function safeTrim(
  value: string | null | undefined | unknown,
  options?: { returnEmptyString?: boolean },
): string | undefined {
  const returnEmptyString = options?.returnEmptyString ?? false

  // Handle null/undefined
  if (value == null) {
    return returnEmptyString ? '' : undefined
  }

  // Convert to string if not already
  const stringValue = typeof value === 'string' ? value : String(value)

  // Trim the string
  const trimmed = stringValue.trim()

  // Return empty string or undefined based on options
  if (trimmed === '') {
    return returnEmptyString ? '' : undefined
  }

  return trimmed
}
